import $ from "jquery";
import MenuObject from "./menu-object";
import util from "../util";
import {
  AllowedElement,
  ComponentType,
  Edge,
  FADE_IN_DURATION,
  FADE_OUT_DURATION,
  Frame, getConfig,
  InteractionType,
  TargetType
} from "./interactable-constants";
import store from "../store";
import {MoveableOptions} from "react-moveable/types";
import {OnResize, OnResizeStart, OnScale, OnScaleStart} from "moveable";
import Interactable from "./interactable";

export function getComponentType(target: TargetType) {
  const $el = $(target as AllowedElement);
  if (MenuObject.isMenuObject($el)) return ComponentType.MENU_OBJECT;
  if (util.isText($el)) {
    if (util.isWrappedText($el)) {
      return ComponentType.CURVED_TEXT;
    } else {
      return ComponentType.TEXT;
    }
  }
  if (util.isImage($el)) return ComponentType.IMAGE;
  if (util.isQrCode($el)) return ComponentType.QR;
  if (util.isSquare($el)) return ComponentType.SQUARE;
  if (util.isCircle($el)) return ComponentType.CIRCLE;
  if (util.isLine($el)) return ComponentType.LINE;
  if (util.isFramePreview($el)) return ComponentType.FRAME_PREVIEW;
  if (util.isFrame($el) && !util.isFramePreview($el)) return ComponentType.FRAME;
  if (util.isIllustration($el)) return ComponentType.ILLUSTRATION;
  if (util.isGroup($el)) return ComponentType.GROUP;

  return ComponentType.GENERIC;
}

export function getOptionsByElement(target: TargetType): MoveableOptions {
  let ops = {};
  if (target) {
    if (!Array.isArray(target)) {
      const componentType = getComponentType(target);
      ops = getConfig(componentType);

      if (!Object.keys(ops).length) {
        ops = getConfig(ComponentType.GENERIC);
      }
    }
  }
  return ops;
}

export function getPageZoom() {
  return store.getState().zoom / 100;
}

export function getUndoLabel(type: InteractionType) {
  switch (type) {
    case InteractionType.DRAG:
      return 'Drag';
    case InteractionType.RESIZE:
      return 'Resize';
    case InteractionType.ROTATE:
      return 'Rotate';
    case InteractionType.SCALE:
      return 'Scale';
    default:
      return 'Undo';
  }
}

export function isElementLocked($el: JQuery<AllowedElement>) {
  return util.isLocked($el);
}

export function isElementFavorite($el: JQuery<AllowedElement>) {
  return true;
}

export function showAngle() {
  $('.moveable-angle').fadeIn(FADE_IN_DURATION);
}

export function hideAngle() {
  $('.moveable-angle').fadeOut(FADE_OUT_DURATION);
}

export function showDimensions() {
  $('.moveable-dimension').fadeIn(FADE_IN_DURATION);
}

export function hideDimensions() {
  $('.moveable-dimension').fadeOut(FADE_OUT_DURATION);
}

export function showFavorite() {
  $('.moveable-favorite').fadeIn(FADE_IN_DURATION);
}

export function hideFavorite() {
  $('.moveable-favorite').fadeOut(FADE_OUT_DURATION);
}

function deltaTransformPoint(matrix: DOMMatrix, point: { [key: string]: number }) {
  const dx = point.x * matrix.a + point.y * matrix.c + 0;
  const dy = point.x * matrix.b + point.y * matrix.d + 0;
  return {x: dx, y: dy};
}

export function decomposeMatrix(matrix: DOMMatrix) {
  // @see https://gist.github.com/2052247

  // calculate delta transform point
  const px = deltaTransformPoint(matrix, {x: 0, y: 1});
  const py = deltaTransformPoint(matrix, {x: 1, y: 0});

  // calculate skew
  const skewX = ((180 / Math.PI) * Math.atan2(px.y, px.x) - 90);
  const skewY = ((180 / Math.PI) * Math.atan2(py.y, py.x));

  return {
    translateX: matrix.e,
    translateY: matrix.f,
    scaleX: Math.sqrt(matrix.a * matrix.a + matrix.b * matrix.b),
    scaleY: Math.sqrt(matrix.c * matrix.c + matrix.d * matrix.d),
    skewX: skewX,
    skewY: skewY,
    rotation: skewX // rotation is the same as skew x
  };
}

export function getCursorByEdge(e: Edge) {
  switch (e) {
    case Edge.BOTTOM_RIGHT:
    case Edge.TOP_LEFT:
      return 'nwse-resize';
    case Edge.BOTTOM_LEFT:
    case Edge.TOP_RIGHT:
      return 'nesw-resize';
    case Edge.LEFT:
    case Edge.RIGHT:
      return 'ew-resize';
    case Edge.TOP:
    case Edge.BOTTOM:
      return 'ns-resize';
    default:
      return '';
  }
}

export function deepCopy(obj: any, parse: boolean = false) {
  const stringified = JSON.stringify(obj);
  return parse ? JSON.parse(stringified) : stringified;
}

export function isTopLeftDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return e.direction[0] === -1 && e.direction[1] === -1;
}

export function isTopRightDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return e.direction[0] === 1 && e.direction[1] === -1;
}

export function isBottomRightDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return e.direction[0] === 1 && e.direction[1] === 1;
}

export function isBottomLeftDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return e.direction[0] === -1 && e.direction[1] === 1;
}

export function isLeftDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return e.direction[0] === -1 && e.direction[1] === 0;
}

export function isRightDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return e.direction[0] === 1 && e.direction[1] === 0;
}

export function isLeftRightDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return isLeftDrag(e) || isRightDrag(e);
}

export function isTopDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return e.direction[0] === 0 && e.direction[1] === -1;
}

export function isBottomDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return e.direction[0] === 0 && e.direction[1] === 1;
}

export function isTopBottomDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return isTopDrag(e) || isBottomDrag(e);
}

export function isCornerDrag(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  return !isLeftRightDrag(e) && !isTopBottomDrag(e);
}

export function getDraggedEdge(e: OnScale | OnResize | OnResizeStart | OnScaleStart) {
  if (isTopLeftDrag(e)) return Edge.TOP_LEFT;
  if (isTopRightDrag(e)) return Edge.TOP_RIGHT;
  if (isBottomLeftDrag(e)) return Edge.BOTTOM_LEFT;
  if (isBottomRightDrag(e)) return Edge.BOTTOM_RIGHT;
  if (isLeftDrag(e)) return Edge.LEFT;
  if (isRightDrag(e)) return Edge.RIGHT;
  if (isTopDrag(e)) return Edge.TOP;
  if (isBottomDrag(e)) return Edge.BOTTOM;

  return Edge.NA;
}

export function rotateMoveableCursor(event: JQuery.MouseOverEvent) {
  const $target = $(event.target);
  let i = Interactable.getInstanceFromControlBox($target);

  if (i) {
    let rotation = i.getRotation();
    const direction = $target.attr('data-direction');
    if (direction === Edge.TOP_RIGHT) {
      rotation = rotation
    } else if (direction === Edge.RIGHT) {
      rotation += 45;
    } else if (direction === Edge.BOTTOM_RIGHT) {
      rotation += 90;
    } else if (direction === Edge.BOTTOM) {
      rotation += 135;
    } else if (direction === Edge.BOTTOM_LEFT) {
      rotation += 180;
    } else if (direction === Edge.LEFT) {
      rotation += 225;
    } else if (direction === Edge.TOP_LEFT) {
      rotation += 270;
    } else if (direction === Edge.TOP) {
      rotation += 315;
    }
    rotation = rotation % 360;
    let cursorString = `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='21' height='21' viewBox='0 0 20 20'%3e%3cg transform='rotate(${rotation} 9 9)'%3e%3cpath fill='white' d='M11.7 9.1l-4 4.1 2.8 2.8H2V7.5l2.9 2.8 4-4 1.4-1.4L7.5 2H16v8.5l-2.8-2.8z'/%3e%3cpath d='M10.7 8.7l-4.4 4.5L8.1 15H3V9.9l1.9 1.9 4.4-4.5 2.5-2.4L9.9 3H15v5.1l-1.8-1.8z'/%3e%3c/g%3e%3c/svg%3e") 8 8, pointer`;
    event.target.style.setProperty('cursor', cursorString, 'important');
  }
}

export function getDefaultFrame() {
  return {
    translate: [0, 0],
    scale: [1, 1],
    rotate: 0
  } as Frame;
}

export function documentContainsElement($el: JQuery<AllowedElement>) {
  // @ts-ignore
  return $.contains(window.document, $el[0]);
}

export function formatNumber(nr: number) {
  return Math.round(nr * 100000) / 100000;
}
